import { render as renderTemplate, setCopyLocale, type CopyId, type CopyVarMap, type VarlessCopyId } from '@npc/copy'

/**
 * 文案层。**屏里只许出现模板 id，不许出现句子。**
 *
 * 这是 PRD §8 的安全要求落到前端的样子：所有面向用户的文案由固定模板 + 变量生成，
 * 填空的人（人或模型）改不了句式。前端这一侧的义务是「不自己造句」。
 *
 * 句子的唯一来源是 `docs/copy/*.yaml`，经 `tools/gen-copy.mjs` 生成 `@npc/copy`，
 * 与 Go 端（`packages/shared-go/copytext`）逐字节一致并有交叉验证。
 * 这一层只剩两件事：**把变量里的数字转成字符串**，以及**切 `**加粗**`**。
 *
 * 三道关都是生成物给的：
 *   id 写错          编译期，不在 `CopyId` 联合类型里
 *   变量漏传多传      编译期
 *   变量值不干净      运行期，emoji / 术语黑名单 / 控制符 / 超长，任一不过就抛 CopyError
 *
 * 注意：真正会变的那些句子（机器用途、状态、告警、探测流、统计块）**不走这里**——
 * 它们由后端按模板渲染好放在响应里，前端照原样显示。契约里每个这样的字段旁边
 * 都有一个 `copy_template` 说明它是哪条模板生成的。
 */

export type { CopyId }

/** 需要变量的那些 id。 */
type VarfulCopyId = Exclude<CopyId, VarlessCopyId>

/**
 * 某个 id 要哪些变量。
 *
 * 变量名按生成物定死（漏一个多一个都是编译期错误），值放宽到 `string | number`：
 * 机器台数、天数这类本来就是数字，让每个调用点写一遍 `String(n)` 只是噪音——
 * 转换在 `render` 里做一次。
 */
export type VarsFor<K extends CopyId> = { [P in keyof CopyVarMap[K]]: string | number }

/**
 * 渲染一条模板。
 *
 * 故意只有这一个入口，没有 `format` 之类能自由拼句子的东西——
 * 有了拼句子的能力，「文案只来自模板」就只是一句自觉。
 *
 * 两个重载不是啰嗦：不带变量的模板**不许**传第二个参数，带变量的**必须**传。
 * 用一个 `vars?: ...` 糊过去的话，漏传变量就退化成运行期才炸——
 * 那正是这一层存在的理由被抹掉的地方。
 */
export function render<K extends VarlessCopyId>(id: K): string
export function render<K extends VarfulCopyId>(id: K, vars: VarsFor<K>): string
export function render(id: CopyId, vars?: Record<string, string | number>): string {
  return renderTemplate(id as VarfulCopyId, stringify(vars ?? {}) as CopyVarMap[VarfulCopyId])
}

/** `render` 的短名字。JSX 之外（placeholder、aria-label、title）用它。 */
export const t = render

function stringify(vars: Record<string, string | number>): Record<string, string> {
  return Object.fromEntries(Object.entries(vars).map(([key, value]) => [key, String(value)]))
}

/** 一段渲染好的文案切成「普通」与「加粗」两种片段。`**这样**` 是加粗。 */
export interface CopySegment {
  text: string
  strong: boolean
}

/**
 * 把 `**...**` 切出来。
 *
 * 这个记号不是这里发明的：`docs/copy/auth.yaml` 的 `auth.recovery_codes.detail`
 * 原文就写着 `**这些码只显示这一次**`，模板层本来就用它标重点。
 */
export function segments<K extends VarlessCopyId>(id: K): CopySegment[]
export function segments<K extends VarfulCopyId>(id: K, vars: VarsFor<K>): CopySegment[]
export function segments(id: CopyId, vars?: Record<string, string | number>): CopySegment[] {
  return (render as (id: CopyId, vars?: Record<string, string | number>) => string)(id, vars)
    .split(/\*\*/)
    .map((text, index) => ({ text, strong: index % 2 === 1 }))
    .filter((segment) => segment.text.length > 0)
}

/**
 * 不做编译期检查的那条路，**只给 `T` 组件用**。
 *
 * 泛型函数体里没法拿 `K` 去对上面那组重载做重解析，而 `T` 又必须是泛型的
 * （它要按 id 判断 vars 该不该必填）。所以这里留一个口子，
 * 但屏走 `render` / `segments`，编译期该红的照样红。
 */
export function segmentsUnchecked(
  id: CopyId,
  vars?: Record<string, string | number>,
): CopySegment[] {
  return (segments as (id: CopyId, vars?: Record<string, string | number>) => CopySegment[])(id, vars)
}

// ---------------------------------------------------------------- 多语言（L1）

export type Lang = 'zh' | 'en'

const LANG_KEY = 'npc-lang'

/** 现在的语言。渲染层在模块加载时按它初始化。 */
export function currentLang(): Lang {
  try {
    const saved = window.localStorage.getItem(LANG_KEY)
    if (saved === 'zh' || saved === 'en') return saved
  } catch {
    // 读不到就给默认
  }
  return 'zh'
}

/**
 * 切语言并整页刷新。
 *
 * 为什么是刷新而不是热切：t() 是模块级函数、全站上百个调用点都在渲染期取文，
 * 不引入 context 改造的情况下，刷新是唯一能保证**每一处**都换语言的办法。
 * 换语言是一次罕见的动作，刷新一次完全可接受（R5 L2 再考虑无刷新方案）。
 */
export function applyLang(lang: Lang): void {
  try {
    window.localStorage.setItem(LANG_KEY, lang)
  } catch {
    // 存不上就只改本次会话
  }
  window.location.reload()
}

// 模块加载即初始化渲染层语言——所有 t() 调用之前。
setCopyLocale(currentLang())
if (typeof document !== 'undefined') {
  document.documentElement.lang = currentLang() === 'en' ? 'en' : 'zh-CN'
}
